import { useEffect } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import WhatsAppButton from '../../components/feature/WhatsAppButton';
import ScrollToTopButton from '../../components/feature/ScrollToTopButton';
import FAQHero from './components/FAQHero';
import FAQAccordion from './components/FAQAccordion';
import FAQCta from './components/FAQCta';

const SCHEMA_FAQ = JSON.stringify({
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'WebPage',
      '@id': `${import.meta.env.VITE_SITE_URL}/faq#webpage`,
      url: `${import.meta.env.VITE_SITE_URL}/faq`,
      name: 'Perguntas Frequentes sobre AVCB, CLCB e Segurança do Trabalho | Engeporto',
      description: 'Tire suas dúvidas sobre AVCB, CLCB, PGR, LTCAT, perícias técnicas e treinamentos de segurança do trabalho em Sorocaba e Campinas.',
      inLanguage: 'pt-BR',
      isPartOf: { '@id': `${import.meta.env.VITE_SITE_URL}/#website` },
    },
    {
      '@type': 'FAQPage',
      '@id': `${import.meta.env.VITE_SITE_URL}/faq#faqpage`,
      mainEntity: [
        {
          '@type': 'Question',
          name: 'O que é AVCB e quando é obrigatório?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'O Auto de Vistoria do Corpo de Bombeiros (AVCB) é o documento que atesta que uma edificação atende às normas de segurança contra incêndio. É obrigatório para edificações com área superior a 750 m² ou com características específicas definidas pelo Decreto Estadual nº 63.911/2018 em São Paulo.',
          },
        },
        {
          '@type': 'Question',
          name: 'Qual a diferença entre AVCB e CLCB?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'O AVCB é emitido para edificações de maior porte e risco, enquanto o CLCB (Certificado de Licença do Corpo de Bombeiros) é destinado a edificações de menor porte e risco reduzido. Ambos atestam conformidade com as normas de segurança contra incêndio.',
          },
        },
        {
          '@type': 'Question',
          name: 'O que é PGR e quem é obrigado a ter?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'O Programa de Gerenciamento de Riscos (PGR) é exigido pela NR-01 para todas as empresas que possuam empregados regidos pela CLT. Ele substitui o antigo PPRA e deve conter o inventário de riscos ocupacionais e o plano de ação.',
          },
        },
        {
          '@type': 'Question',
          name: 'Quais regiões a Engeporto atende?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'A Engeporto Engenharia atende principalmente as Regiões Metropolitanas de Sorocaba e Campinas, com sede em Porto Feliz — SP.',
          },
        },
      ],
    },
  ],
});

const FAQPage = () => {
  useEffect(() => {
    document.title = 'Perguntas Frequentes | AVCB, CLCB, PGR e Segurança do Trabalho — Engeporto';
    const setMeta = (name: string, content: string, prop = false) => {
      const sel = prop ? `meta[property="${name}"]` : `meta[name="${name}"]`;
      const el = document.querySelector(sel) as HTMLMetaElement | null;
      if (el) el.setAttribute('content', content);
    };
    setMeta('description', 'Tire suas dúvidas sobre AVCB, CLCB, PGR, LTCAT, perícias técnicas e treinamentos de segurança do trabalho nas Regiões Metropolitanas de Sorocaba e Campinas. Engeporto Engenharia.');
    setMeta('keywords', 'FAQ AVCB, dúvidas CLCB, PGR obrigatório, LTCAT, segurança trabalho Sorocaba, perícia técnica Campinas');
    setMeta('og:title', 'Perguntas Frequentes | AVCB, CLCB, PGR — Engeporto Sorocaba Campinas', true);
    setMeta('og:description', 'Dúvidas sobre AVCB, CLCB, PGR, LTCAT e segurança do trabalho em Sorocaba e Campinas.', true);
    setMeta('og:url', `${import.meta.env.VITE_SITE_URL}/faq`, true);

    // Canonical
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', `${import.meta.env.VITE_SITE_URL}/faq`);
    return () => { canonical?.remove(); };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: SCHEMA_FAQ }}
      />
      <Navbar />
      <FAQHero />
      <FAQAccordion />
      <FAQCta />
      <Footer />
      <WhatsAppButton />
      <ScrollToTopButton />
    </div>
  );
};

export default FAQPage;